import React, { useEffect, useState } from "react";
import { Prompt } from "react-router-dom";
import { TextField, Switch, Box, Grid, Backdrop, Button } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { object, string } from "yup";
import {
  addNewYard,
  getAllYardAction,
  updateYard,
} from "src/app-redux/configuration/actions/configurationActions";
import {
  loader,
  selectAllYardsList,
  setisDirtyValue,
} from "src/app-redux/configuration/configurationSlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import { IAddYard } from "src/common/typings/configuration";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonButton from "../../../../../../components/CommonButton/CommonButton";
import { columns } from "./columns";
import { useStyles } from "./styles";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonLoader from "src/components/Loader/CommonLoader";

export interface IForm extends IAddYard {}
interface Props {}

export function InputFields({}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [isEditMode, setisEditMode] = React.useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  const loading = useAppSelector(loader);
  const allYards = useAppSelector(selectAllYardsList);
  // Form Start
  const validationSchema = object({
    name: string().required("This field is required").max(50, "Maximum 50 characters are allowed."),
  });
  const formik = useFormik<IForm>({
    enableReinitialize: true,
    initialValues: {
      name: "",
      isActive: false,
      isContainerYard: true,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, actions) => {
      let res;
      if (isEditMode) res = await dispatch(updateYard(values));
      else res = await dispatch(addNewYard(values));
      if (res?.payload) {
        dispatch(getAllYardAction(true));
        actions.setSubmitting(false);
        actions.resetForm();
        setisEditMode(false);
        setIsDirtyValue(false);
        setSelectedRecord(null);
      }
    },
  });

  const handleUpdateClick = (params: any) => {
    setisEditMode(true);
    setSelectedRecord(params);
    formik.setFieldValue("id", params.id);
    formik.setFieldValue("name", params.name);
    formik.setFieldValue("isActive", params.isActive);
  };
  useEffect(() => {
    dispatch(getAllYardAction(true));
  }, []);
  /* Transform Column */
  useEffect(() => {
    if (allYards.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        setIsDirtyValue(true);
                        handleUpdateClick(row.original);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column.accessorKey === "rowName"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return <>{`Row ${row.original.rowName}`}</>;
              },
            }
          : column.accessorKey === "isActive"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isActive ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isActive ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [allYards]);

  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);
  function handleChange(e: any) {
    formik.handleChange(e);
    e.target.value ? setIsDirtyValue(true) : setIsDirtyValue(false);
  }

  function setIsDirtyValue(val: boolean) {
    setIsDirty(val);
    dispatch(setisDirtyValue(val));
  }

  return (
    <>
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          {/* First Section */}
          <>
            <Grid className={classes.UserMainPanel}>
              <Grid
                // Container
                container
                className={classes.mainConainer}
                direction="row"
                alignItems="center"
                spacing={2}
              >
                <Grid container item xs={12} lg={7} spacing={2}>
                  <Grid item xs={12} md={7} style={{ paddingLeft: "0px", paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Add Yard Name"
                        name="name"
                        placeholder="Add Yard Name"
                        disabled={false}
                        value={formik.values.name}
                        onChange={handleChange}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3} style={{ paddingLeft: "12px" }}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formik.values.isActive)}
                            onChange={formik.handleChange}
                            name="isActive"
                            color="primary"
                          />
                        }
                        label="Active"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2} style={{ paddingRight: 0 }}>
                    <Box mb={2} mt={1} style={{ float: "right" }}>
                      <CommonButton
                        isGreen
                        cssClass="customSubmitBtn"
                        disable={
                          selectedRecord
                            ? formik.values.name === selectedRecord.name &&
                              formik.values.isActive === selectedRecord.isActive
                            : JSON.stringify(formik.values) === JSON.stringify(formik.initialValues)
                        }
                        onClick={() => {
                          formik.submitForm();
                        }}
                      >
                        {isEditMode ? "Update" : "Add"}
                      </CommonButton>
                    </Box>
                  </Grid>
                  <CommonMaterialGrid
                    defaultPerPageRecord={10}
                    height={400}
                    rows={allYards || []}
                    columns={displayColumns}
                  />
                </Grid>
                {/* End Inner Container */}
              </Grid>
              {/* End Outer Container */}
            </Grid>
          </>
        </Grid>
      </form>
    </>
  );
}
